import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from '../utils/axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Base64url decoder utility to extract JWT claims without external packages
const decodeToken = (token) => {
  try {
    const payload = token.split('.')[1];
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return decoded;
  } catch (e) {
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Sync logout across tabs
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'token' && !e.newValue) {
        console.log('🔄 Logout synchronized from another tab');
        setUser(null);
        delete axios.defaults.headers.common['Authorization'];
        if (window.location.pathname !== '/login') {
          localStorage.setItem('session_expired_message', 'true');
          window.location.href = '/login';
        }
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Validate session on boot
  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');

    if (token && userData) {
      try {
        const decoded = decodeToken(token);
        if (decoded && decoded.exp && decoded.exp * 1000 < Date.now()) {
          console.log('🔐 Stored token has expired. Clearing session.');
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.setItem('session_expired_message', 'true');
        } else {
          const parsedUser = JSON.parse(userData);
          setUser(parsedUser);
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        }
      } catch (error) {
        console.error('Error parsing stored user data:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  // Absolute 24h timer / absolute session tracking
  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem('token');
    if (!token) return;

    const decoded = decodeToken(token);
    if (decoded && decoded.exp) {
      const timeRemaining = decoded.exp * 1000 - Date.now();
      if (timeRemaining > 0) {
        const timer = setTimeout(() => {
          console.log('⏰ Active session expired, logging out.');
          logout();
          localStorage.setItem('session_expired_message', 'true');
          window.location.href = '/login';
        }, timeRemaining);
        return () => clearTimeout(timer);
      } else {
        console.log('⏰ Token already expired upon effect running, logging out.');
        logout();
        localStorage.setItem('session_expired_message', 'true');
        window.location.href = '/login';
      }
    }
  }, [user]);


  const login = async (identifier, password) => {
    try {
      console.log('🔐 Attempting API login with identifier:', identifier);
      // Unified login endpoint for both admin and employee
      const response = await axios.post('/auth/login', {
        identifier,
        password
      });

      const { access_token, user: userData } = response.data;
      console.log('✅ API login successful:', userData);
      console.log('🎯 Environment:', userData.isSandbox ? 'SANDBOX' : 'LIVE');

      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      // Update state synchronously before returning
      setUser(userData);
      return { success: true, user: userData };
    } catch (error) {
      console.log('❌ API login failed:', error.message);
      console.log('Error details:', error.response?.data);
      return {
        success: false,
        error: error.response?.data?.error || 'Invalid credentials'
      };
    }
  };

  const logout = async () => {
    try {
      await axios.post('/auth/logout');
    } catch (err) {
      console.warn('Server logout failed or offline:', err.message);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);

      // Prevent back button access
      window.history.pushState(null, null, window.location.href);
      window.onpopstate = () => {
        window.history.pushState(null, null, window.location.href);
      };
    }
  };

  const value = {
    user,
    login,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
